import React from 'react';
import i18n from '../../../../i18n';
import { SideBar } from '../../sidebar';
import NoData from '../../../common/NoData';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import StatusTabs from '../../../common/StatusTab';
import HeaderBanner from '../../../common/HeaderBanner';
import { getLoggedUser } from '../../../../state/user';
import { getDriversList } from './services/DriversListService';
import { Users, Plus, Search, Truck, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DriversTable } from './components/DriversTable';

// Import your drivers table component (you'll need to create this)

export class DriversList extends React.Component {
    constructor() {
        super();
        this.state = {
            searchTerm: '',
            drivers: [],
            status: 'all', // all, active, pending, rejected
            input: {},
            statusData: [
                { key: 'all', label: 'All', count: 0 },
                { key: 'active', label: 'Active', count: 0 },
                { key: 'pending', label: 'Pending', count: 0 },
                { key: 'rejected', label: 'Rejected', count: 0 },
            ],
            is_loading: false,
            limit: 15,
            page: 1,
            total: 0,
            hideSidebar: false,
            showModal: false,
            stats: {
                total: 0,
                active: 0,
                pending: 0,
                rejected: 0,
                opal: 0,
                nonOpal: 0
            }
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        const params = new URLSearchParams(window.location.search);
        const status = params.get('status') || 'all';
        const page = parseInt(params.get('page')) || 1;
        const limit = parseInt(params.get('limit')) || 15;
        const search = params.get('search') || '';

        this.setState({ status, page, limit, searchTerm: search }, () => {
            this.fetchDrivers();
        });
    }

    updateUrlQuery(params) {
        const queryString = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== '' && v !== null))
        ).toString();
        window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
    }

    async fetchDrivers() {
        this.setState({ is_loading: true });

        const { limit, page, status, searchTerm } = this.state;

        try {
            // Prepare API params
            const params = {
                page,
                per_page: limit,
                search: searchTerm || undefined
            };

            // Add status filter if not "all"
            if (status !== 'all') {
                params.status = status.toUpperCase(); // Convert to backend format (P, A, R)
            }

            const response = await getDriversList();

            if (response && response.success) {
                const drivers = response.data.data || [];
                const pagination = response.data.pagination || {};

                // Calculate stats
                const stats = {
                    total: drivers.length,
                    active: drivers.filter(d => d.status === 'A').length,
                    pending: drivers.filter(d => d.status === 'P').length,
                    rejected: drivers.filter(d => d.status === 'R').length,
                    opal: drivers.filter(d => d.opal === true).length,
                    nonOpal: drivers.filter(d => d.opal === false).length
                };

                this.setState({
                    drivers: drivers,
                    total: pagination.total || drivers.length,
                    page: pagination.current_page || page,
                    stats: stats,
                    statusData: [
                        { key: 'all', label: 'All', count: stats.total },
                        { key: 'active', label: 'Active', count: stats.active },
                        { key: 'pending', label: 'Pending', count: stats.pending },
                        { key: 'rejected', label: 'Rejected', count: stats.rejected },
                    ],
                    is_loading: false,
                });

                // Update URL
                this.updateUrlQuery({
                    status: status !== 'all' ? status : '',
                    page: page !== 1 ? page : '',
                    limit: limit !== 15 ? limit : '',
                    search: searchTerm || ''
                });

            } else {
                console.error('Failed to fetch drivers:', response?.message);
                this.setState({
                    drivers: [],
                    is_loading: false
                });
            }
        } catch (error) {
            console.error('Error fetching drivers:', error);
            this.setState({
                drivers: [],
                is_loading: false
            });
        }
    }

    handleTabChange = (key) => {
        this.setState({ status: key, page: 1 }, () => {
            this.fetchDrivers();
        });
    };

    handleSearch = (e) => {
        const searchTerm = e.target.value;
        this.setState({ searchTerm, page: 1 }, () => {
            // Debounce search to avoid too many API calls
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.fetchDrivers();
            }, 500);
        });
    };

    onChangePage = (page) => {
        this.setState({ page }, () => {
            this.fetchDrivers();
        });
    };

    onChangeRowsPerPage = (limit) => {
        this.setState({ limit, page: 1 }, () => {
            this.fetchDrivers();
        });
    };

    handleRefresh = () => {
        this.fetchDrivers();
    };

    handleApproveDriver = async (driver) => {
        try {
            // TODO: Call approve API
            // await approveDriver(driver.id);
            alert(`Driver ${driver.name} approved successfully!`);
            this.fetchDrivers(); // Refresh the list
        } catch (error) {
            console.error('Error approving driver:', error);
            alert('Failed to approve driver');
        }
    };

    handleRejectDriver = async (driver) => {
        const reason = prompt('Enter rejection reason:');
        if (!reason) {
            alert('Rejection reason is required');
            return;
        }

        try {
            // TODO: Call reject API
            // await rejectDriver(driver.id, reason);
            alert(`Driver ${driver.name} rejected successfully!`);
            this.fetchDrivers(); // Refresh the list
        } catch (error) {
            console.error('Error rejecting driver:', error);
            alert('Failed to reject driver');
        }
    };

    handleDeleteDriver = async (driver) => {
        if (!window.confirm(`Are you sure you want to delete driver ${driver.name}?`)) {
            return;
        }

        try {
            // TODO: Call delete API
            // await deleteDriver(driver.id);
            alert(`Driver ${driver.name} deleted successfully!`);
            this.fetchDrivers(); // Refresh the list
        } catch (error) {
            console.error('Error deleting driver:', error);
            alert('Failed to delete driver');
        }
    };

    render() {
        const user = getLoggedUser();
        const { drivers, is_loading, statusData, status, page, limit, total, } = this.state;

        return (
            <React.Fragment>
                <HeaderBanner
                    title={i18n.t('drivers_list')}
                    subtitle={i18n.t('manage_drivers_list')}
                />
                <section className="mt-5">
                    <div className="container">
                        <div className="row">
                            <div className="col-xl-3 col-lg-12 col-md-12">
                                {!this.state.hideSidebar && <SideBar />}
                            </div>
                            <div
                                className={
                                    this.state.hideSidebar
                                        ? 'col-xl-12 col-lg-12 col-md-12'
                                        : 'col-xl-9 col-lg-12 col-md-12'
                                }
                            >
                                <div className="card">
                                    <div className="card-header">
                                        <div className="expand-dashboard">
                                            <span
                                                style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                                                onClick={() => {
                                                    this.setState({
                                                        hideSidebar: !this.state.hideSidebar,
                                                    });
                                                }}
                                            >
                                                {this.state.hideSidebar && <i className="icon-size-actual"></i>}
                                                {!this.state.hideSidebar && <i className="icon-size-fullscreen"></i>}
                                            </span>
                                        </div>
                                        <div className="expand-dashboard">
                                            <Link to="/driver-registration" className='btn no-data-button'><User /> {i18n.t('driver_registration')}</Link>
                                        </div>

                                    </div>

                                    <div className="card-body">


                                        <StatusTabs
                                            tabs={statusData}
                                            onTabChange={this.handleTabChange}
                                            selected={status}
                                        />

                                        <div className="Data-Listing">
                                            {is_loading ? (
                                                <TableSkeleton columns={8} rows={10} />
                                            ) : drivers.length ? (
                                                <DriversTable
                                                    drivers={drivers}
                                                    currentPage={page}
                                                    limit={limit}
                                                    total={total}
                                                    onChangePage={this.onChangePage}
                                                    onChangeRowsPerPage={this.onChangeRowsPerPage}
                                                    onApprove={this.handleApproveDriver}
                                                    onReject={this.handleRejectDriver}
                                                    onDelete={this.handleDeleteDriver}
                                                    onRefresh={this.handleRefresh}
                                                    userRole={user?.role}
                                                    history={this.props.history}
                                                />
                                            ) : (
                                                <NoData
                                                    message="No drivers found"
                                                    actionText="Refresh"
                                                    onAction={this.handleRefresh}
                                                    buttonText="Add New Driver"
                                                    onButtonClick={() => this.props.history.push('/driver-registration')}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

            </React.Fragment>
        );
    }
}

// Add some CSS for stats
const styles = `
.stats-summary {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
}

.stat-card {
    text-align: center;
    padding: 15px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: transform 0.2s;
}

.stat-card:hover {
    transform: translateY(-2px);
}

.stat-card h3 {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 5px;
}

.stat-card p {
    color: #666;
    font-size: 14px;
    margin: 0;
}

.search-box {
    display: flex;
    align-items: center;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 5px 10px;
}

.search-box input {
    border: none;
    outline: none;
    margin-left: 8px;
    width: 100%;
}

.search-box svg {
    color: #666;
}
`;

// Add styles to document head
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);